import Icon from '../Icon/Icon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useService } from '../../containers/UseService';
import {
    campaignsActions,
    notificationsActions,
    screensActions,
} from '../../store';
import {
    calculateSpentPercentage,
    formatDateAccordingToBrowser,
    formatNumber,
    isNowPM,
} from '../../utils/functions';
import { getStrategyTranslationKey } from '../../utils/strategy';
import {
    EVENLY_SCHEDULER_MODE,
    PACING_STATES,
    formatPercent,
    getFlightProgress,
    isEvenlyMode,
} from '../../utils/spendPacing';
import useCampaignSpendStatus from '../../hooks/stats/useCampaignSpendStatus';
import CommonDropdown from '../Buttons/CommonDropdown';
import SwitcherButton from '../Buttons/CommonSwitcher';
import CustomTableDragSelect from '../Campaign/WizardBasics/Timing/CustomTableDragSelect/CustomTableDragSelect';
import CopyText from '../CopyText/CopyText';
import PredictionDetails from '../DashboardIndividual/Predictions/PredictionDetails/PredictionDetails';
import ProgressBar from '../Progress/ProgressBar';
import PacingPill from '../SpendPacing/PacingPill';

function CampaignCard(params) {
    const { t } = useTranslation();
    const dashboard = useSelector((state) => state.dashboard);
    const service = useService();
    const dispatch = useDispatch();
    const history = useHistory();
    const currencies = useSelector((state) => state.wallet.currencies);
    const [formatedCurrency, setFormatedCurrency] = useState('');
    const {
        campaign,
        isCardExpanded,
        screenStatus,
        dashboardFromHome,
        statsByCampaignId,
        userScreens,
    } = params;

    const [isActiveSwitch, setisActiveSwitch] = useState(campaign.isActive);
    const id = campaign.id;
    const setIsFetching = params.setIsFetching;
    const setIsCloned = params.setIsCloned;
    const status = params.status;
    const searchRefresh = params.searchRefresh;
    const buyingTime = isNowPM() ? 'pm' : 'am';
    const campaignPrediction = dashboard.campaignPrediction;
    const isDelayed =
        campaignPrediction.endDate > 1 || campaignPrediction.endDate < -1;
    const pacingTranslationKey = getStrategyTranslationKey({
        strategy: campaign?.scheduler_mode,
    });

    const [showPredictions, setShowPredictions] = useState(true);

    const showLoadingWhile = async (callback) => {
        dispatch(campaignsActions.setIsLoading(true));
        await callback();
        dispatch(campaignsActions.setIsLoading(false));
    };

    const [stats, setStats] = useState({});

    const [showScheduleModal, setShowScheduleModal] = useState(false);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') setShowScheduleModal(false);
        };
        if (showScheduleModal) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showScheduleModal]);

    const campaignStats = statsByCampaignId?.[String(id)] || null;

    useEffect(() => {
        if (!campaignStats) {
            setStats({});
            return;
        }

        setStats({
            ...campaignStats,
            spentProgress: calculateSpentPercentage(
                campaignStats.budget,
                campaignStats.spent
            ),
        });
    }, [campaignStats]);

    //it cleans screens filters before send the user to edit campaign
    const editCampaign = () => {
        dispatch(screensActions.resetByKey('maxCpm'));
        dispatch(screensActions.resetByKey('selectedScreensLocal'));
        dispatch(screensActions.resetByKey('isFilterByAudiences'));
        dispatch(screensActions.resetByKey('showSelectedScreens'));
        dispatch(screensActions.resetByKey('selectedScreenTypes'));
        dispatch(screensActions.resetByKey('selectedPublishers'));
        dispatch(screensActions.resetByKey('selectedTiers'));
        history.push(`/campaign/edit/${campaign.id}`);
    };

    const getScreenName = (screenId) => {
        if (!screenId || !userScreens) return '';
        const screen = userScreens.get(screenId);
        return screen?.name || screen?.label || '';
    };

    const duplicateCampaign = async () => {
        try {
            await showLoadingWhile(() =>
                service.duplicateCampaign(campaign, status)
            );
            let currentUrl = window.location.pathname;
            // update search if it's in draft
            if (searchRefresh && currentUrl.includes('draft')) {
                const inputSearch = document.getElementById(
                    'input-search-campaigns'
                ).value;
                await searchRefresh(inputSearch);
            }
            dispatch(
                notificationsActions.setNotification({
                    type: 'success',
                    title:
                        t('sections.campaign-list.card-campaign.clone') +
                        ' ' +
                        campaign.id,
                    message: t(
                        'sections.campaign-list.card-campaign.success-clone'
                    ),
                })
            );
            setIsCloned(true);
            setIsFetching(true);
        } catch (error) {
            console.error(error);
            dispatch(campaignsActions.setIsLoading(false));
        }
    };

    const restoreCampaign = () => {
        showLoadingWhile(() => service.restoreCampaign(campaign));
        setIsFetching(true);
    };

    const deleteCampaign = async () => {
        try {
            await showLoadingWhile(() =>
                service.deleteCampaign(campaign, status)
            );
            if (searchRefresh) {
                const inputSearch = document.getElementById(
                    'input-search-campaigns'
                ).value;
                await searchRefresh(inputSearch);
            }
            dispatch(
                notificationsActions.setNotification({
                    type: 'success',
                    title:
                        t(
                            'sections.campaign-list.card-campaign.delete-campaign'
                        ) +
                        ' ' +
                        campaign.id,
                    message: t('sections.campaign-list.success-on-delete'),
                })
            );
            setIsFetching(true);
        } catch (error) {
            console.error(error);
            dispatch(campaignsActions.setIsLoading(false));
        }
    };

    const activateOrDeactivate = () => {
        showLoadingWhile(async () => {
            if (campaign.isActive) {
                await service.deactivateCampaign(campaign);
            } else {
                await service.activateCampaign(campaign);
            }
            setisActiveSwitch(true);
        });
    };

    const campaignList = [
        {
            icon: 'eye',
            label: t('sections.campaign-list.card-campaign.view-in-dashboard'),
            id: id,
            action: 'link',
            function: null,
            path: '/dashboard/',
            style: null,
        },
        {
            icon: 'pencil',
            label: t('sections.campaign-list.card-campaign.edit-dashboard'),
            id: id,
            action: 'function',
            function: editCampaign,
            path: null,
            style: null,
        },
        {
            icon: 'copy',
            label: t('sections.campaign-list.card-campaign.clone-campaign'),
            id: id,
            action: 'function',
            function: duplicateCampaign,
            path: null,
            style: null,
        },
        {
            icon: 'trash',
            type: 'delete',
            label: t('sections.campaign-list.card-campaign.delete-campaign'),
            message: 'sections.campaign-list.warning',
            id: id,
            action: 'function',
            function: deleteCampaign,
            path: null,
            style: 'text-red-500',
        },
    ];

    const restoreList = [
        {
            icon: 'trash-restore',
            label: t('sections.campaign-list.card-campaign.restore'),
            id: id,
            action: 'function',
            function: restoreCampaign,
            path: null,
            style: null,
        },
    ];

    useEffect(() => {
        if (currencies.length > 0) {
            const currency = currencies.find(
                (currency) => currency.id === campaign.currency_id
            );
            if (currency) setFormatedCurrency(currency.symbol);
        }
    }, [currencies]);

    useEffect(() => {
        if (isActiveSwitch) {
            setisActiveSwitch(campaign.isActive);
        }
    }, [isActiveSwitch]);

    const hasPendingScreens =
        screenStatus?.status?.pendingScreens?.total > 0 ||
        screenStatus?.status?.disapprovedScreens?.total > 0;
    const pendingScreensIcon = () => {
        if (hasPendingScreens) {
            return (
                <Icon
                    onClick={() => setShowPredictions(!showPredictions)}
                    name="exclamation-triangle"
                    fixedWidth
                    className="ml-2 text-yellow-500 cursor-pointer"
                />
            );
        }
    };

    const hasPredictions = Object.keys(campaignPrediction) > 0;

    // Spend pacing: only active campaigns get the flight marker, and only
    // scheduler_mode 1 is evaluated by spend-status; the rest show N/A.
    const isActiveCampaign = campaign.status === 'ACTIVE';
    const isEvenlyCampaign = isEvenlyMode(campaign.scheduler_mode);
    const { data: spendStatus } = useCampaignSpendStatus(id, {
        enabled: isActiveCampaign && isEvenlyCampaign,
    });
    const dailyPacing = spendStatus?.status?.daily ?? null;
    const flightProgress = isActiveCampaign
        ? getFlightProgress({
              startPeriod: campaign.start_period,
              endPeriod: campaign.end_period,
              daily: dailyPacing,
          })
        : null;
    const flightMarker = flightProgress
        ? {
              percent: flightProgress.percent,
              label: t('sections.spend-pacing.marker-label', {
                  day: flightProgress.day,
                  total: flightProgress.totalDays,
                  percent: formatPercent(flightProgress.percent),
              }),
          }
        : null;

    const pacingPill = () => {
        if (!isActiveCampaign) return null;
        if (!isEvenlyCampaign) {
            return (
                <PacingPill
                    kind="na"
                    title={t('sections.spend-pacing.not-applicable-tooltip', {
                        strategy: t(
                            getStrategyTranslationKey({
                                strategy: EVENLY_SCHEDULER_MODE,
                            })
                        ),
                    })}
                />
            );
        }
        if (!PACING_STATES[dailyPacing?.state]) return null;
        return (
            <PacingPill
                state={dailyPacing.state}
                title={t('sections.spend-pacing.card-tooltip', {
                    spent: formatPercent(dailyPacing.spent_pct),
                    expected: formatPercent(dailyPacing.expected_pct),
                })}
            />
        );
    };

    return (
        <>
            <div className="group module m-1 mb-4 w-full p-2">
                <div className="flex-col">
                    <div className="header flex justify-between">
                        <div className={'title text-sm font-light w-full'}>
                            {hasPendingScreens && (
                                <PredictionDetails
                                    campaignScreensStatus={screenStatus}
                                    campaign={campaign}
                                    setHandleDataSchedule={setShowPredictions}
                                    handleDataSchedule={showPredictions}
                                    isDelayed={isDelayed}
                                    campaignPrediction={
                                        hasPredictions
                                            ? campaignPrediction
                                            : null
                                    }
                                />
                            )}
                            <Link to={`/dashboard/${id}`}>
                                <span className="badge font-normal font-mono text-gray-500">
                                    #{id}
                                </span>{' '}
                                <span className="font-semibold">
                                    {' '}
                                    {campaign.brand}
                                </span>{' '}
                                <span>{campaign.name}</span>
                            </Link>
                            {/* {isDelayed ? (
                                <>
                                    <ReactTooltip
                                        effect="solid"
                                        className="w-48 p-2"
                                        id="etaTooltip"
                                        aria-haspopup="true"
                                    >
                                        {campaignPrediction.endDate > 0
                                            ? t(
                                                  "sections.dashboard-individual.predictions.increase-spending"
                                              )
                                            : t(
                                                  "sections.dashboard-individual.predictions.decrease-spending"
                                              )}
                                    </ReactTooltip>
                                    <a data-tip data-for='etaTooltip' >
                                        <Icon
                                        name="exclamation-triangle"
                                        fixedWidth
                                        className="mx-2 text-red-300"
                                        />
                                    </a>
                                </>
                            ) : null } */}
                            {pendingScreensIcon()}
                        </div>
                        <div className="actions flex justify-end items-center">
                            <div className="mr-2 flex items-center">
                                {pacingPill()}
                            </div>
                            {(campaign.status === 'ACTIVE' ||
                                campaign.status === 'INACTIVE') && (
                                <SwitcherButton
                                    onToggle={activateOrDeactivate}
                                    status={isActiveSwitch}
                                    labelOn={t(
                                        'sections.campaign-list.card-campaign.active'
                                    )}
                                    labelOff={t(
                                        'sections.campaign-list.card-campaign.inactive'
                                    )}
                                />
                            )}
                            {campaign.status !== 'DELETED' ? (
                                <CommonDropdown
                                    icon={'cog'}
                                    list={campaignList}
                                />
                            ) : (
                                <CommonDropdown
                                    icon={'cog'}
                                    list={restoreList}
                                    isDeleted
                                />
                            )}
                        </div>
                    </div>
                    <div className="w-full">
                        <ProgressBar
                            progress={
                                stats?.spentProgress
                                    ? stats.spentProgress
                                    : campaign.spentProgress
                            }
                            isActive={campaign.isActive}
                            marker={flightMarker}
                        />
                    </div>
                </div>
                <div
                    className={`body bg-gray-50 mt-2 rounded-lg flex flex-wrap items-stretch space-x-2 divide-y divide-x divide-dotted overflow-y-auto opacity-0 h-0 group-hover:h-52 group-hover:opacity-100 transition-all ease-in-out duration-150 delay-300 ${
                        isCardExpanded ? 'h-52 opacity-100' : 'h-0 opacity-0'
                    }`}>
                    <div className="p-2 flex-grow">
                        <div className="title font-light text-sm text-gray-600">
                            {t(
                                'sections.campaigns.new-campaign.wizard-basics.budget.title'
                            )}
                        </div>
                        <div
                            className={`${
                                campaign.status === 'ACTIVE' ? '' : 'hidden'
                            } text-xs text-gray-400`}>
                            {t(
                                'sections.campaign-list.card-campaign.budget-explain'
                            )}
                        </div>
                        <div className="font-semibold text-lg">
                            <span className="text-xs font-thin text-gray-500">
                                {formatedCurrency}
                            </span>{' '}
                            {campaign.status === 'ACTIVE' ||
                            campaign.status === 'JOB_SUCCESS' ? (
                                <>
                                    {formatNumber(
                                        campaignStats?.spent != null
                                            ? campaignStats.spent
                                            : campaign.totalSpent,
                                        'rounded'
                                    )}{' '}
                                    <span className="font-light text-sm">
                                        /{' '}
                                        <span className="text-xs font-thin text-gray-500">
                                            {formatedCurrency}
                                        </span>{' '}
                                        {formatNumber(
                                            campaignStats?.budget != null
                                                ? campaignStats.budget
                                                : campaign.budget,
                                            'currency'
                                        )}
                                    </span>
                                </>
                            ) : (
                                formatNumber(campaign.budget, 'currency')
                            )}
                        </div>
                        {/* FIXME: Uncomment code when the CPM is operational */}
                        {/* <div className="font-semibold text-lg">
                            <div className="font-light text-sm text-gray-600">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-screens.screens.table-screenCPM'
                                )}
                                :{' '}
                                {campaign.max_cpm === 0 && (
                                    <span>
                                        {t(
                                            'sections.campaign-list.card-campaign.without-cpm'
                                        )}
                                    </span>
                                )}{' '}
                                {campaign.max_cpm !== 0 && (
                                    <>
                                        <span className="text-xs font-thin text-gray-500">
                                            {' '}
                                            {formatedCurrency}
                                        </span>{' '}
                                        <span>
                                            {formatNumber(
                                                campaign.max_cpm,
                                                'currency'
                                            )}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div> */}
                    </div>
                    <div
                        className={`p-2 ${
                            campaign.status === 'ACTIVE' ? '' : 'hidden'
                        }`}>
                        <div className="title font-light text-sm text-gray-600">
                            {t(
                                'sections.campaigns.new-campaign.wizard-review.blocks.basics.impacts'
                            )}
                        </div>
                        <div className="text-xs text-gray-400">
                            {t(
                                'sections.campaign-list.card-campaign.impacts-explain'
                            )}
                        </div>
                        <div className="font-semibold text-lg">
                            <div className="font-semibold text-lg">
                                {formatNumber(
                                    campaignStats?.impacts != null
                                        ? campaignStats.impacts
                                        : 0,
                                    'rounded'
                                )}
                            </div>
                        </div>
                    </div>
                    <div
                        className={`p-2 ${
                            campaign.status === 'ACTIVE' ? '' : 'hidden'
                        }`}>
                        <div className="title font-light text-sm text-gray-600">
                            {t(
                                'sections.campaign-list.card-campaign.campaign-status'
                            )}
                        </div>
                        <div className="text-xs text-gray-400">
                            {t(
                                'sections.campaign-list.card-campaign.screen-status'
                            )}
                        </div>
                        <ul>
                            <li className="text-xs text-gray-600">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-screens.screens.pending'
                                )}
                                : {screenStatus?.status?.pendingScreens?.total}
                            </li>
                            <li className="text-xs text-gray-600">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-screens.screens.approve'
                                )}
                                : {screenStatus?.status?.approvedScreens?.total}
                            </li>
                            <li className="text-xs text-gray-600">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-screens.screens.disapproved'
                                )}
                                :{' '}
                                {
                                    screenStatus?.status?.disapprovedScreens
                                        ?.total
                                }
                            </li>
                        </ul>
                    </div>
                    <div className="p-2 flex-grow">
                        <div className="title font-light text-sm text-gray-600">
                            {t(
                                'sections.campaigns.new-campaign.wizard-basics.timing.pacing'
                            ) + ':'}
                            <br />
                            <span className="font-semibold">
                                {t(pacingTranslationKey)}
                            </span>
                        </div>
                    </div>
                    <div className="w-full">
                        <div className="flex flex-row items-center gap-6">
                            <div className="flex items-center gap-0.5">
                                <span className="text-sm text-gray-400 font-semibold">
                                    {t(
                                        'sections.campaign-list.card-campaign.from'
                                    )}
                                    :
                                </span>
                                <span className="font-light text-black text-sm ml-0.5">
                                    {formatDateAccordingToBrowser(
                                        campaign.start_period
                                    )}
                                </span>
                            </div>
                            <div className="flex items-center gap-0.5">
                                <span className="text-sm text-gray-400 font-semibold">
                                    {t(
                                        'sections.campaign-list.card-campaign.to'
                                    )}
                                    :
                                </span>
                                <span className="font-light text-black text-sm ml-0.5">
                                    {formatDateAccordingToBrowser(
                                        campaign.end_period
                                    )}
                                </span>
                            </div>
                            <div>
                                <button
                                    type="button"
                                    onClick={() => setShowScheduleModal(true)}
                                    className="inline-flex items-center px-3 py-1 rounded-full border bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium shadow-sm hover:shadow"
                                    aria-haspopup="dialog"
                                    aria-expanded={showScheduleModal}
                                    data-testid="schedule-button">
                                    <span className="text-xs">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-basics.timing.day-parting'
                                        )}{' '}
                                    </span>
                                </button>
                            </div>
                        </div>
                        <div className="mt-0">
                            <div className="flex flex-col">
                                <div className="text-2xs text-gray-400 mb-0.5 mt-2">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-screens.screens.screens-title'
                                    )}
                                </div>

                                <div className="text-left">
                                    <ul className="list-none space-y-0">
                                        {campaign.screen.map(
                                            (screenId, index) => (
                                                <li
                                                    key={index}
                                                    className="flex flex-row space-x-2 py-0">
                                                    <div className="text-2xs text-gray-600">
                                                        <CopyText
                                                            text={
                                                                screenId.toUpperCase() +
                                                                ' - ' +
                                                                getScreenName(
                                                                    screenId
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {showScheduleModal && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Cronograma Schedule">
                        {/* backdrop */}
                        <div
                            className="absolute inset-0 bg-black/50"
                            onClick={() => setShowScheduleModal(false)}
                        />

                        {/* modal box */}
                        <div
                            className="
                                relative bg-white rounded-lg shadow-2xl
                                p-3 z-10
                                max-w-fit
                                max-h-[80vh]
                            "
                            onClick={(e) => e.stopPropagation()}>
                            {/* header */}
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-400">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-basics.timing.day-parting'
                                    )}{' '}
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setShowScheduleModal(false)}
                                    className="p-1 rounded hover:bg-gray-100">
                                    <Icon
                                        name="times"
                                        fixedWidth
                                        className="text-gray-500 h-5 hover:text-black"
                                    />
                                </button>
                            </div>

                            {/* content */}
                            <div className="overflow-auto max-h-[70vh] flex justify-center">
                                <div className="origin-top-left">
                                    <CustomTableDragSelect
                                        readOnly={true}
                                        change={() => {}}
                                        campaign={campaign}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default CampaignCard;
